import type { HookAPI } from 'antd/es/modal/useModal'
import { useCallback, useRef, useState } from 'react'
import { toast } from 'sonner'

import { handleApiError } from '@/utils/api/errors/handleApiError'

import { stockImportsApi } from '../../api/stockImportsApi'
import { STOCK_IMPORT_PROGRESS_MESSAGES } from '../../lib/stockImportsMessages'
import type {
  ConfigPreviewResponse,
  StockImportConfigRequest,
} from '../../types/stockImports.types'

export const useConfigPreview = (modal: HookAPI) => {
  const [preview, setPreview] = useState<ConfigPreviewResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const requestVersionRef = useRef(0)

  const invalidate = useCallback(() => {
    requestVersionRef.current += 1
    setPreview(null)
    setLoading(false)
  }, [])

  const loadPreview = async (
    payload: StockImportConfigRequest,
    configId: string | null
  ) => {
    const requestVersion = ++requestVersionRef.current
    const toastId = toast.loading(STOCK_IMPORT_PROGRESS_MESSAGES.preview)
    setLoading(true)
    try {
      const result = await stockImportsApi.previewConfig(
        { ...payload, configId },
        payload.priceSettings?.enabled === true
      )
      if (requestVersion !== requestVersionRef.current) {
        toast.dismiss(toastId)
        return
      }
      setPreview(result)
      if (result.success) toast.success(result.message, { id: toastId })
      else toast.error(result.message, { id: toastId })
    } catch (error) {
      toast.dismiss(toastId)
      if (requestVersion !== requestVersionRef.current) return
      handleApiError(error, modal)
    } finally {
      if (requestVersion === requestVersionRef.current) setLoading(false)
    }
  }

  return { preview, loading, invalidate, loadPreview }
}
